import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useProjectScopeStore } from '../../scope/projectScopeStore';
import { projectIdsContaining } from './boardProjectScope';
import { listRepos } from '../../graph/projectsStore';
import { reposOf } from '../../services/git/scopeProjects';
import { localPathOf } from '../../services/git/codeRepoClones';
import { codeRepoErrorKind, loadCodeRepoLog } from '../../services/git/codeRepoCommits';
import { commitsForKey } from '../../services/git/commitTaskRefs';
import { mergeTaskCommits } from '../../extensions/bundled/task-manager/taskCommitLinks';
import { isTauri } from '../../utils/isTauri';

const EMPTY = [];

/**
 * The repositories of the projects that hold board `boardId` (project
 * `contains board:<id>` + `contains git_repo:<id>` links), each with the folder
 * to read on this device. Reads the repo records of each project's scope.
 */
export function useBoardRepos(boardId) {
  const known = useProjectScopeStore((s) => s.known);
  const projects = known?.projects || EMPTY;
  const links = known?.links || EMPTY;
  const projectIds = useMemo(() => [...projectIdsContaining(`board:${boardId}`, links)], [boardId, links]);
  const [repos, setRepos] = useState(null); // null = reading
  const sig = projectIds.join('|');

  useEffect(() => {
    let alive = true;
    const inBoard = projects.filter((p) => projectIds.includes(String(p.id)));
    if (!inBoard.length) {
      setRepos([]);
      return undefined;
    }
    setRepos(null);
    (async () => {
      const byId = new Map();
      for (const p of inBoard) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const scopeRepos = await listRepos(p.scopeKey);
          for (const r of reposOf(p.id, links, scopeRepos)) if (!byId.has(String(r.id))) byId.set(String(r.id), r);
        } catch {
          /* a scope that cannot be read adds no repositories */
        }
      }
      if (alive) setRepos([...byId.values()]);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `sig` is the project set; links/projects change identity on every scope reload
  }, [sig, projects.length, links.length]);

  return { projectIds, repos };
}

/**
 * Commits linked to the task across the board's repositories, read from the
 * local clones (codeRepoCommits.js): those whose message or branch names one
 * of `taskKeys` (current key + old keys), plus the task's hand-made links. Per repository a state:
 * 'loading' | 'ready' | 'noClone' (linked by URL, no folder picked on this
 * device) | 'error' (+ kind: missing / notGit / refused / other).
 */
export default function useTaskCommits({ boardId, taskKeys, commitLinks = EMPTY }) {
  const desktop = isTauri();
  const { projectIds, repos } = useBoardRepos(boardId);
  const [states, setStates] = useState({}); // repo id → { status, log?, error?, kind? }
  const [nonce, setNonce] = useState(0);
  const refreshRef = useRef(false);

  useEffect(() => {
    if (!desktop || !repos) return undefined;
    const run = { alive: true };
    const refresh = refreshRef.current;
    refreshRef.current = false;
    const next = {};
    for (const r of repos) next[r.id] = localPathOf(r) ? { status: 'loading' } : { status: 'noClone' };
    setStates(next);
    const put = (id, state) => {
      if (run.alive) setStates((s) => ({ ...s, [id]: state }));
    };
    for (const r of repos) {
      const path = localPathOf(r);
      if (!path) continue;
      loadCodeRepoLog(path, { refresh })
        .then((log) => put(r.id, { status: 'ready', log }))
        .catch((e) => put(r.id, { status: 'error', kind: codeRepoErrorKind(e), error: String(e?.message || e) }));
    }
    return () => {
      run.alive = false;
    };
  }, [desktop, repos, nonce]);

  const refresh = useCallback(() => {
    refreshRef.current = true;
    setNonce((n) => n + 1);
  }, []);

  // Every commit of every readable repository, with its repo (the "Link commit" picker).
  const allCommits = useMemo(() => {
    if (!repos) return [];
    const out = [];
    for (const r of repos) {
      const st = states[r.id];
      if (st?.status !== 'ready') continue;
      for (const c of st.log.commits) out.push({ ...c, repo: r, repoId: r.id, remoteUrl: r.remote || st.log.remoteUrl || '' });
    }
    return out.sort((a, b) => (b.time || 0) - (a.time || 0));
  }, [repos, states]);

  // Automatic links (the key — or an old key of a renamed board — in the message or branch)
  // merged with the task's hand-made ones (taskCommitLinks.js).
  const keySig = (Array.isArray(taskKeys) ? taskKeys : [taskKeys]).filter(Boolean).join('|');
  const { shown: commits, hidden } = useMemo(() => {
    const keys = keySig ? keySig.split('|') : [];
    const derived = keys.length ? commitsForKey(allCommits, keys) : [];
    const reposById = new Map((repos || []).map((r) => [String(r.id), r]));
    const known = new Map(allCommits.map((c) => [`${c.repo.id}:${c.oid}`, c]));
    return mergeTaskCommits(derived, commitLinks, { reposById, known });
  }, [allCommits, keySig, commitLinks, repos]);

  const loading = !repos || Object.values(states).some((s) => s.status === 'loading');
  const truncated = Object.values(states).some((s) => s.status === 'ready' && s.log.truncated);
  return { desktop, projectIds, repos: repos || EMPTY, states, commits, hidden, allCommits, loading, truncated, refresh };
}
